/**
 * API service for task manager
 * Handles all communication with the backend
 */
import config from '../../config';

// Base URL for API requests
const API_BASE_URL = config.api.baseUrl;

/**
 * Clean MongoDB ID to ensure compatibility
 * @param {string|any} id - The ID to clean
 * @returns {string|null} Cleaned MongoDB ID or null
 */
const cleanMongoId = (id) => {
  if (!id) return null;
  const str = id.toString();
  // Extract 24-character hex ID if embedded in a longer string
  const hexMatch = str.match(/[0-9a-f]{24}/i);
  return hexMatch ? hexMatch[0] : str.replace(/[^0-9a-f]/gi, '');
};

/**
 * Check if data is mock data from the backend during DB disconnection
 * @param {Object} data - Data to check
 * @returns {boolean} Whether the data is mock data
 */
const isMockData = (data) => {
  return data && data._isMock === true;
};

/**
 * Make an API request with consistent error handling
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Fetch options
 * @returns {Promise<any>} Response data
 */
const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  const defaultHeaders = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'Origin': window.location.origin
  };
  
  try {
    console.log(`API Request: ${url}`, options);
    
    const response = await fetch(url, {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers
      },
      mode: 'cors'
    });
    
    // Handle non-OK responses
    if (!response.ok) {
      try {
        const errorData = await response.json();
        console.error(`API Error (${response.status}):`, errorData);
        throw new Error(errorData.error || `API error: ${response.status}`);
      } catch (jsonError) {
        // If we can't parse JSON, use the status text
        const errorText = await response.text();
        console.error(`API Error (${response.status}):`, errorText);
        throw new Error(errorText || `API error: ${response.status}`);
      }
    }
    
    // For empty responses or 204 status
    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return {};
    }
    
    // Parse the response, with error handling
    try {
      const data = await response.json();
      
      // Log if it's mock data
      if (isMockData(data)) {
        console.warn('Received mock data from backend (database may be offline)');
      }
      
      return data;
    } catch (jsonError) {
      console.error('Error parsing JSON response:', jsonError);
      return {}; // Return empty object as fallback
    }
  } catch (error) {
    console.error(`API request failed for ${endpoint}:`, error);
    throw error;
  }
};

// Board API

/**
 * Get all boards
 * @returns {Promise<Array>} Boards
 */
export const getBoards = async () => {
  try {
    // Try to use the enhanced boards endpoint first which might include task counts
    try {
      const data = await apiRequest('/api/boards-with-counts');
      return Array.isArray(data) ? data : [];
    } catch (enhancedError) {
      console.warn('Enhanced boards endpoint failed, falling back to standard endpoint:', enhancedError);
      
      // Fall back to standard endpoint
      const data = await apiRequest('/boards');
      return Array.isArray(data) ? data : [];
    }
  } catch (error) {
    console.error('Error fetching boards:', error);
    return []; // Return empty array on error
  }
};

/**
 * Get a specific board
 * @param {string} id - Board ID
 * @returns {Promise<Object>} Board
 */
export const getBoard = async (id) => {
  if (!id) {
    console.error('Board ID is required');
    return { columns: [] }; // Return minimal structure on error
  }
  
  try {
    const cleanId = cleanMongoId(id);
    console.log(`Getting board with cleaned ID: ${cleanId}`);
    
    // Use the new enhanced endpoint that returns a board with all its tasks
    const response = await apiRequest(`/api/board-with-tasks/${cleanId}`);
    
    // The new endpoint returns { board, taskCount }
    const boardData = response.board || response;
    
    // Ensure board has the expected structure
    if (!boardData) return { columns: [] };
    if (!boardData.columns) boardData.columns = [];
    if (!Array.isArray(boardData.columns)) boardData.columns = [];
    
    // Ensure each column has taskIds as an array
    boardData.columns = boardData.columns.map(column => {
      // Create a normalized column structure
      const normalizedColumn = {
        ...column,
        taskIds: Array.isArray(column.taskIds) ? column.taskIds : [],
        tasks: Array.isArray(column.tasks) ? column.tasks : []
      };
      
      return normalizedColumn;
    });
    
    // Extract tasks from all columns to create a flat tasks array on the board
    const allTasks = [];
    boardData.columns.forEach(column => {
      if (Array.isArray(column.tasks)) {
        allTasks.push(...column.tasks);
      }
    });
    
    // Add the flat tasks array to the board
    boardData.tasks = allTasks;
    
    // If we have a taskCount from the new API, add it to the board
    if (response.taskCount !== undefined) {
      boardData.taskCount = response.taskCount;
    }
    
    return boardData;
  } catch (error) {
    console.error(`Error fetching board ${id}:`, error);
    return { columns: [] }; // Return minimal structure on error
  }
};

/**
 * Create a new board
 * @param {Object} board - Board data
 * @returns {Promise<Object>} Created board
 */
export const createBoard = async (board) => {
  try {
    // Format board according to the API guide
    const boardPayload = {
      title: board.title,
      description: board.description || `Board created on ${new Date().toLocaleDateString()}`,
      // If columns are provided, ensure they have the required structure
      columns: board.columns ? board.columns.map(column => ({
        id: column.id,
        title: column.title,
        taskIds: Array.isArray(column.taskIds) ? column.taskIds : []
      })) : [
        { id: "todo", title: "To Do", taskIds: [] },
        { id: "in-progress", title: "In Progress", taskIds: [] },
        { id: "completed", title: "Completed", taskIds: [] }
      ]
    };
    
    return await apiRequest('/boards', {
      method: 'POST',
      body: JSON.stringify(boardPayload)
    });
  } catch (error) {
    console.error('Error creating board:', error);
    throw error;
  }
};

/**
 * Update a board
 * @param {string} id - Board ID
 * @param {Object} updates - Board updates
 * @returns {Promise<Object>} Updated board
 */
export const updateBoard = async (id, updates) => {
  if (!id) {
    throw new Error('Board ID is required');
  }
  
  try {
    const cleanId = cleanMongoId(id);
    console.log(`Updating board with cleaned ID: ${cleanId}`);
    
    // Ensure columns have the right structure if present
    const updatesPayload = { ...updates };
    
    if (updatesPayload.columns) {
      updatesPayload.columns = updatesPayload.columns.map(column => ({
        ...column,
        id: column.id,
        title: column.title,
        taskIds: Array.isArray(column.taskIds) ? column.taskIds : []
      }));
    }
    
    return await apiRequest(`/boards/${cleanId}`, {
      method: 'PUT',
      body: JSON.stringify(updatesPayload)
    });
  } catch (error) {
    console.error(`Error updating board ${id}:`, error);
    throw error;
  }
};

/**
 * Delete a board
 * @param {string} id - Board ID
 * @returns {Promise<Object>} Response
 */
export const deleteBoard = async (id) => {
  if (!id) {
    throw new Error('Board ID is required');
  }
  
  try {
    const cleanId = cleanMongoId(id);
    console.log(`Deleting board with cleaned ID: ${cleanId}`);
    
    // Use the new endpoint
    const response = await fetch(`${API_BASE_URL}/api/board/${cleanId}`, {
      method: 'DELETE',
      headers: {
        'Accept': 'application/json'
      }
    });
    
    // Check if response is OK
    if (!response.ok) {
      throw new Error(`Server returned error: ${response.status}`);
    }
    
    // Handle empty responses
    const text = await response.text();
    if (!text) {
      return { success: true }; // Assume success for empty responses
    }
    
    // Parse JSON response safely
    try {
      return JSON.parse(text);
    } catch (parseError) {
      console.error('Failed to parse response:', text);
      throw new Error('Invalid JSON response from server');
    }
  } catch (error) {
    console.error(`Error deleting board ${id}:`, error);
    throw error;
  }
};

// Task API

/**
 * Test backend connection
 * @returns {Promise<Object>} Debug info
 */
export const testBackendConnection = async () => {
  try {
    const response = await apiRequest('/debug/frontend-test');
    console.log('Backend connection test:', response);
    return true;
  } catch (error) {
    console.error('Backend connection test failed:', error);
    return false;
  }
};

/**
 * Get all tasks for a board
 * @param {string} boardId - Board ID
 * @returns {Promise<Array>} Tasks
 */
export const getTasks = async (boardId) => {
  if (!boardId) {
    console.error('Board ID is required for getTasks');
    return []; // Return empty array if no boardId
  }
  
  try {
    const cleanId = cleanMongoId(boardId);
    console.log(`Fetching tasks for board with cleaned ID: ${cleanId}`);
    
    // Try to use the new enhanced tasks endpoint first
    try {
      const data = await apiRequest(`/api/enhanced-tasks/${cleanId}`);
      
      // Ensure we always return an array
      const tasks = Array.isArray(data) ? data : [];
      console.log(`Successfully processed ${tasks.length} tasks for board ${boardId} from enhanced endpoint`);
      
      return tasks;
    } catch (enhancedError) {
      console.warn('Enhanced tasks endpoint failed, falling back to standard endpoint:', enhancedError);
      
      // Fall back to the standard endpoint
      const data = await apiRequest(`/tasks/board/${cleanId}`);
      
      // Ensure we always return an array
      const tasks = Array.isArray(data) ? data : [];
      console.log(`Successfully processed ${tasks.length} tasks for board ${boardId} from standard endpoint`);
      
      return tasks;
    }
  } catch (error) {
    console.error(`Error fetching tasks for board ${boardId}:`, error);
    return []; // Return empty array on error
  }
};

/**
 * Get a single task
 * @param {string} id - Task ID
 * @returns {Promise<Object>} Task
 */
export const getTask = async (id) => {
  if (!id) {
    console.error('Task ID is required');
    return {}; // Return empty object if no id
  }
  
  try {
    const cleanId = cleanMongoId(id);
    console.log(`Fetching task with cleaned ID: ${cleanId}`);
    
    const task = await apiRequest(`/tasks/${cleanId}`);
    return task || {};
  } catch (error) {
    console.error(`Error fetching task ${id}:`, error);
    return {}; // Return empty object on error
  }
};

/**
 * Create a new task
 * @param {Object} task - Task data
 * @returns {Promise<Object>} Created task
 */
export const createTask = async (task) => {
  if (!task.boardId) {
    throw new Error('Board ID is required for creating a task');
  }
  
  if (!task.title && !task.text) {
    throw new Error('Task title/text is required');
  }
  
  try {
    // Clean the board ID
    const cleanId = cleanMongoId(task.boardId);
    console.log(`Creating task for board with cleaned ID: ${cleanId}`);
    
    // Prepare the task payload
    const taskPayload = {
      ...task,
      title: task.text || task.title, // Support both text and title fields
      boardId: cleanId,
      // Keep the original columnId as-is
      columnId: task.columnId || 'todo' // Default to todo if not specified
    };
    
    console.log('Sending task to API:', taskPayload);
    
    // Use the new simple task creation endpoint that returns both task and updated board
    try {
      const response = await apiRequest('/api/simple-create-task', {
        method: 'POST',
        body: JSON.stringify(taskPayload)
      });
      
      console.log('Task creation response:', response);
      
      // The new endpoint returns { success, task, board, message }
      if (response.success && response.task) {
        // If we have the updated board, store it for easy access
        if (response.board) {
          response.task._updatedBoard = response.board;
        }
        return response.task;
      }
      
      return response.task || response;
    } catch (enhancedError) {
      console.warn('Enhanced task creation endpoint failed, falling back to standard endpoint:', enhancedError);
      
      // Fall back to the resilient task creation endpoint
      return await apiRequest('/api/create-task', {
        method: 'POST',
        body: JSON.stringify(taskPayload)
      });
    }
  } catch (error) {
    console.error('Error creating task:', error);
    throw error;
  }
};

/**
 * Update a task
 * @param {string} id - Task ID
 * @param {Object} updates - Task updates
 * @returns {Promise<Object>} Updated task
 */
export const updateTask = async (id, updates) => {
  if (!id) {
    throw new Error('Task ID is required');
  }
  
  try {
    const cleanId = cleanMongoId(id);
    console.log(`Updating task with cleaned ID: ${cleanId}`);
    
    const url = `https://taskmangerback-t2b1.vercel.app/tasks/${cleanId}`;
    
    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(updates)
    });
    
    if (!response.ok) {
      throw new Error(`Server returned error: ${response.status}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`Error updating task ${id}:`, error);
    throw error;
  }
};

/**
 * Delete a task
 * @param {string} id - Task ID
 * @returns {Promise<Object>} Response
 */
export const deleteTask = async (id) => {
  if (!id) {
    throw new Error('Task ID is required');
  }
  
  try {
    const cleanId = cleanMongoId(id);
    console.log(`Deleting task with cleaned ID: ${cleanId}`);
    
    // Use the new endpoint
    const response = await fetch(`${API_BASE_URL}/api/task/${cleanId}`, {
      method: 'DELETE',
      headers: {
        'Accept': 'application/json'
      }
    });
    
    // Check if response is OK
    if (!response.ok) {
      throw new Error(`Server returned error: ${response.status}`);
    }
    
    // Handle empty responses
    const text = await response.text();
    if (!text) {
      return { success: true }; // Assume success for empty responses
    }
    
    // Parse JSON response safely
    try {
      return JSON.parse(text);
    } catch (parseError) {
      console.error('Failed to parse response:', text);
      throw new Error('Invalid JSON response from server');
    }
  } catch (error) {
    console.error(`Error deleting task ${id}:`, error);
    throw error;
  }
};

/**
 * Move a task to a different column
 * @param {string} id - Task ID
 * @param {string} columnId - Column ID
 * @returns {Promise<Object>} Updated task
 */
export const moveTask = async (id, columnId) => {
  if (!id) {
    throw new Error('Task ID is required');
  }
  
  if (!columnId) {
    throw new Error('Column ID is required');
  }
  
  try {
    const cleanId = cleanMongoId(id);
    console.log(`Moving task with cleaned ID: ${cleanId} to column: ${columnId}`);
    
    return await apiRequest(`/tasks/${cleanId}/move`, {
      method: 'PATCH',
      body: JSON.stringify({ columnId })
    });
  } catch (error) {
    console.error(`Error moving task ${id} to column ${columnId}:`, error);
    throw error;
  }
};

/**
 * Check for connection to the backend
 * @returns {Promise<boolean>} Whether connected to the backend
 */
export const checkConnection = async () => {
  try {
    const data = await apiRequest('/debug/frontend-test');
    return true;
  } catch (error) {
    return false;
  }
};

// Export all API functions
export default {
  getBoards,
  getBoard,
  createBoard,
  updateBoard,
  deleteBoard,
  getTasks,
  getTask,
  createTask,
  updateTask,
  deleteTask,
  moveTask,
  testBackendConnection,
  checkConnection,
  isMockData,
  cleanMongoId
}; 